import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { BILLING_API } from '@Constants/backendConstants';
import { initialInvoiceSorting, invoiceColumns } from '@Constants/TableColumns';
import { useLatestInvoiceIds } from '@Hooks/Billing';
import { useListApi } from '@Hooks/useListApi';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { type IHasTableModes } from '@Hooks/useTableModes';
import { EnumFrontendLayoutSetting } from '@Model';
import { InvoicesRepository } from '@Repositories/Billing';
import {
    type ListRequest,
    type ListRequestWithIds,
    SingleSelect,
    type ToolbarOptions,
    useTableRefetch,
} from '@synqo/frontend-component-library';
import { useCallback } from 'react';
import { useClientNavigate } from '../../../Routing/useClientNavigate';
import { type CreateInvoiceTableModes } from './CreateInvoiceTableModes';

interface IFrameLatestInvoicesProps extends IHasTableModes<CreateInvoiceTableModes> {}

export function FrameListLatestInvoices({
    tableModeValue,
    setTableMode,
    availableTableModes,
}: IFrameLatestInvoicesProps) {
    const navigate = useClientNavigate();
    const { refetchRef, refetchTable } = useTableRefetch();

    useRemoteCacheInvalidation('invoices', refetchTable);

    const { loadBatch } = useListApi(InvoicesRepository, BILLING_API, ['Customer', 'Emails']);

    const { latestInvoiceIds: invoiceIds } = useLatestInvoiceIds();

    const tableLoader = useCallback(
        (lr: ListRequest) => {
            const listRequestWithIds: ListRequestWithIds = {
                ...lr,
                include_ids: invoiceIds,
                exclude_ids: [],
            };

            return loadBatch(listRequestWithIds);
        },
        [loadBatch, invoiceIds],
    );

    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...elements,
            <SingleSelect<CreateInvoiceTableModes>
                key="tableMode"
                name="tableMode"
                items={availableTableModes}
                value={tableModeValue}
                onChange={setTableMode}
            />,
        ],
        [availableTableModes, tableModeValue, setTableMode],
    );

    return (
        <TableWithStoredSettingsAndLayoutSettings
            layoutSettingsContext={EnumFrontendLayoutSetting.InvoicesTableLayout}
            name="LatestInvoiceList"
            columns={invoiceColumns}
            loader={tableLoader}
            refetchRef={refetchRef}
            canCreate={false}
            useOnClickNavigation
            fullSize
            initialSorting={initialInvoiceSorting}
            onSelect={(apiInvoice) => {
                navigate(`../${apiInvoice.id}`);
                return true;
            }}
            toolbarElements={toolbarElements}
        />
    );
}
